import type {ProductState, StoreInfo, ViewProduct} from '../../i';
import {degreeParts, degreeWords} from '../../redesign/size-questions';
import {allowsAutomatedDocSizes} from '../doc-sizes-and-categories/document-sizing';

type MarketingProduct = Pick<ViewProduct, 'IsCAF' | 'FixedSizeDisplay' | 'ProductTypeID'>;
type MarketingStore = Pick<StoreInfo, 'default_prod_info_static' | 'default_prod_info_static_degree_template' | 'AutomatedDocSizes' | 'ProductDocSizes'>;
type MarketingState = Pick<ProductState, 'DocSizeID' | 'docWidth' | 'docHeight'>;

const DEGREE_MARKER = '[DegreeType]';
const htmlText = (text: string): string => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** The authored marketing HTML with only its preserved degree placeholder filled from the current selection. */
export const degreeMarketing = (prod: MarketingProduct, si: MarketingStore, ps: MarketingState, portalId: number | undefined, currentGradSchoolID: number): string => {
    const fallback = si.default_prod_info_static ?? '';
    const template = si.default_prod_info_static_degree_template;
    if (portalId !== 0 || prod.IsCAF || ps.DocSizeID <= 0 || !allowsAutomatedDocSizes(prod, si.AutomatedDocSizes) || !template?.includes(DEGREE_MARKER)) return fallback;

    // Same school fallback as doc-size-pulse.GetAvailableDocSizes. The store placement's GradSchoolID does not change when
    // a shopper chooses another school/wordmark, so callers pass the live school explicitly.
    const rows = si.ProductDocSizes ?? [];
    const schoolRows = currentGradSchoolID > 0 ? rows.filter(row => row.GradSchoolID === currentGradSchoolID) : [];
    const available = schoolRows.length > 0 ? schoolRows : rows.filter(row => row.GradSchoolID < 1);
    const selected = available.find(row => row.Id === ps.DocSizeID && row.Doc_Width === ps.docWidth && row.Doc_Height === ps.docHeight);
    if (!selected?.DocSizeName?.trim()) return fallback;

    const words = degreeParts(selected.DocSizeName);
    const text = htmlText(degreeWords(selected.DocSizeName));
    const withArticle = words
        ? template.replace(/\b(a|an)(\s+)\[DegreeType\]/gi, (_match: string, article: string, space: string) => {
              const replacement = words[0] === "Associate's" ? 'an' : 'a';
              const cased = article[0] === article[0].toUpperCase() ? replacement[0].toUpperCase() + replacement.slice(1) : replacement;
              return `${cased}${space}${DEGREE_MARKER}`;
          })
        : template;
    return withArticle.split(DEGREE_MARKER).join(text);
};
